import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import type {
  TransferClientOption,
  TransferLogPage,
  TransferSortDirection,
  TransferSortKey,
} from '@baex/shared';
import { transfers } from '../../api/endpoints';
import type { TransferFilters } from '../../api/endpoints';
import { formatBytes } from '../../lib/format';
import { useTimeFormat } from '../../lib/timezone';
import { cx } from '../../lib/cx';
import {
  ROOT_SUBFOLDER_LABEL,
  displayFileName,
  displaySubfolder,
  isServerFault,
  statusLabel,
} from '../../lib/transferLabels';
import { EmptyState, SkeletonRows, SortableHeader, nextSort } from '../../components/ui/Table';
import type { SortState } from '../../components/ui/Table';
import type { HelpTipItem } from '../../components/ui/HelpTip';
import { Button } from '../../components/ui/Button';
import { ErrorNotice } from '../../components/ui/ErrorNotice';
import { DEFAULT_FILTERS, FilterBar, dateRangeError } from './FilterBar';
import type { TransferFilterState } from './FilterBar';
import { RowDetail } from './RowDetail';
import { SummaryStrip } from './SummaryStrip';

export const PAGE_SIZE = 50;

/** How long Search waits for a pause in typing before it reloads the table. */
export const SEARCH_DEBOUNCE_MS = 250;

/** Every sortable column maps to one of the contract's sort keys (functional spec, API Contract Reference — Transfer history). */
type SortKey = TransferSortKey;

export const DEFAULT_SORT: SortState<SortKey> = { key: 'time', direction: 'desc' };

const COLUMN_COUNT = 7;

/**
 * The STATUS column's legend, verbatim from the table in the "Table" section, in the same
 * lifecycle order as the Status filter — not alphabetical, so the two controls never
 * present the same six words in two different sequences. Six words with no shared prefix
 * are not a vocabulary to infer from context, and `deleted` reads as bad news until you
 * know it is the success case. A list, not a paragraph: rendered as a `<dl>`, one line per
 * status, so the eye can find where one ends and the next begins.
 */
export const STATUS_HELP_ITEMS: readonly HelpTipItem[] = [
  { term: 'stored', description: 'Uploaded and waiting to be picked up.' },
  {
    term: 'replayed',
    description:
      'A retry of an earlier upload with the same idempotency key. Nothing new was stored.',
  },
  { term: 'listed', description: 'A client asked what was waiting for it. Nothing moved.' },
  {
    term: 'deleted',
    description:
      'Downloaded in full and removed from storage — this is what a successful pickup looks like.',
  },
  {
    term: 'error (client)',
    description: 'The request was rejected as sent. The caller can fix it and retry.',
  },
  {
    term: 'error (server)',
    description: 'Something failed on our side. Only a retry, or we, can help.',
  },
];

/**
 * What to try next when nothing matched — naming the filters that are actually narrowing
 * the view.
 *
 * The fixed line ("Widen the date range, or set Client and Status back to All") was
 * advice about three controls regardless of which ones were set: type a filename that
 * does not exist and it told you to widen a date range you had never touched, while
 * saying nothing about the Search box that had in fact excluded everything.
 */
/** Whether anything at all is narrowing the log right now. */
export function hasActiveFilters(filters: TransferFilterState): boolean {
  return (
    filters.client !== 'all' ||
    filters.subfolder !== 'all' ||
    filters.status !== 'all' ||
    filters.from !== '' ||
    filters.to !== '' ||
    filters.q.trim() !== ''
  );
}

export function emptyHint(filters: TransferFilterState): string {
  const active: string[] = [];
  if (filters.client !== 'all') active.push(`Client ${filters.client}`);
  if (filters.subfolder !== 'all')
    active.push(`Subfolder ${filters.subfolder === '' ? ROOT_SUBFOLDER_LABEL : filters.subfolder}`);
  if (filters.status !== 'all') active.push(`Status ${filters.status}`);
  if (filters.from || filters.to) active.push('the date range');
  if (filters.q.trim()) active.push(`Search “${filters.q.trim()}”`);

  if (active.length === 0) return 'Nothing has been uploaded or downloaded yet.';
  const list =
    active.length === 1
      ? active[0]
      : `${active.slice(0, -1).join(', ')} and ${active[active.length - 1]}`;
  return `Currently narrowed by ${list}. Widen or clear it to see more of the log.`;
}

function toApiFilters(
  filters: TransferFilterState,
  sort: SortState<SortKey>,
  offset: number,
): TransferFilters {
  const direction: TransferSortDirection = sort.direction;
  const query: TransferFilters = {
    limit: PAGE_SIZE,
    offset,
    sort: sort.key,
    direction,
  };
  if (filters.client !== 'all') query.client = filters.client;
  // Present-or-absent, not truthy-or-falsy: `(root)` is the empty string and must be sent
  // as `subfolder=`, while "All" omits the parameter entirely (functional spec, API Contract Reference — Transfer history).
  if (filters.subfolder !== 'all') query.subfolder = filters.subfolder;
  if (filters.status !== 'all') query.status = filters.status;
  if (filters.from) query.from = filters.from;
  if (filters.to) query.to = filters.to;
  if (filters.q.trim()) query.q = filters.q.trim();
  return query;
}

export function TransfersPage() {
  // The Clients screen links here pre-filtered to one Client ID (see the "Client list — table and row actions" section, "View").
  const [searchParams] = useSearchParams();
  const initialFilters: TransferFilterState = {
    ...DEFAULT_FILTERS,
    client: searchParams.get('client') ?? DEFAULT_FILTERS.client,
  };

  const [filters, setFilters] = useState<TransferFilterState>(initialFilters);
  const [applied, setApplied] = useState<TransferFilterState>(initialFilters);
  const [sort, setSort] = useState<SortState<SortKey>>(DEFAULT_SORT);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const time = useTimeFormat();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  /**
   * Search waits for a pause in typing; every other control still applies on the change.
   *
   * The "Filter bar" section's "changing any filter immediately reloads the table" is about not making the
   * reader press a button, and a dropdown or a date still reloads the instant it changes.
   * A free-text box is different: `client-acme` was eight round trips, seven of them
   * cancelled the moment they left, each one re-rendering the table under the reader's
   * hands on its way past.
   */
  const [debouncedQuery, setDebouncedQuery] = useState(initialFilters.q);
  useEffect(() => {
    if (filters.q === debouncedQuery) return undefined;
    const timer = window.setTimeout(() => setDebouncedQuery(filters.q), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [filters.q, debouncedQuery]);

  // An inverted date range keeps the last valid result on screen (see the "Filter bar" section).
  const rangeError = dateRangeError(filters);
  useEffect(() => {
    if (!rangeError) setApplied({ ...filters, q: debouncedQuery });
  }, [filters, debouncedQuery, rangeError]);

  // Every Client ID the history mentions, deleted clients included (see the "Filter bar" section).
  const clientList = useQuery<TransferClientOption[]>({
    queryKey: ['transfer-client-options'],
    queryFn: () => transfers.clientOptions(),
  });

  // Sorting and paging belong to the server: the sort is part of the query key, so
  // changing it re-fetches from offset 0 rather than reordering the rows already loaded.
  const log = useInfiniteQuery<TransferLogPage>({
    queryKey: ['transfers', applied, sort],
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) =>
      transfers.list(toApiFilters(applied, sort, pageParam as number), signal),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.entries.length < PAGE_SIZE) return undefined;
      return allPages.reduce((total, page) => total + page.entries.length, 0);
    },
  });

  // Changing any filter, or the sort, reloads from the top and resets scroll position.
  useEffect(() => {
    setExpandedId(null);
    scrollRef.current?.scrollTo({ top: 0 });
  }, [applied, sort]);

  // Filtering, sorting and paging all happen on the server now, so the loaded rows are
  // exactly the rows to render, in exactly the order they arrived.
  const visible = useMemo(
    () => (log.data?.pages ?? []).flatMap((page) => page.entries),
    [log.data],
  );

  const clientOptions = useMemo(
    () => [...(clientList.data ?? [])].sort((a, b) => a.clientId.localeCompare(b.clientId)),
    [clientList.data],
  );

  /**
   * The option list describes the data, not this browser's history: it comes from
   * `GET /admin/api/transfers/subfolders`, scoped to the selected client, so it is the same
   * list for every admin and survives a reload. It cannot be derived from the loaded rows
   * any more — once the server narrows them, only the selected subfolder would remain in
   * the list (functional spec, API Contract Reference — Transfer history). It changes only as new transfers arrive, so it is
   * refreshed lazily rather than on every render.
   */
  const subfolderList = useQuery<string[]>({
    queryKey: ['transfer-subfolder-options', applied.client],
    queryFn: () =>
      transfers.subfolderOptions(applied.client === 'all' ? undefined : applied.client),
    staleTime: 60_000,
  });

  const subfolders = useMemo(() => {
    const distinct = subfolderList.data ?? [];
    const named = distinct.filter((value) => value !== '').sort((a, b) => a.localeCompare(b));
    // The empty string is the folder root, and `(root)` is listed first (see the "Filter bar" section).
    return distinct.includes('') ? ['', ...named] : named;
  }, [subfolderList.data]);

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = log;
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Lazy load: fetch the next 50 as the bottom of the list comes into view.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      { root: scrollRef.current, rootMargin: '40px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  function onSort(key: SortKey) {
    setSort((current) => nextSort(current, key));
  }

  // Resets the debounced Search alongside the rest, so clearing is not itself delayed.
  function clearFilters() {
    setFilters(DEFAULT_FILTERS);
    setDebouncedQuery(DEFAULT_FILTERS.q);
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex shrink-0 items-baseline justify-between">
        <h1 className="text-[15px] font-semibold text-ink">Transfers</h1>
        {/*
          Announced, because changing a filter replaces the whole table with no other
          signal that anything happened: a screen-reader user changed Status and heard
          nothing at all. `aria-live` on a node that is always present, so the count is
          read when it changes rather than when it appears.
        */}
        <p aria-live="polite" className="text-2xs text-ink-muted">
          {log.isFetching && !log.isFetchingNextPage
            ? 'Loading…'
            : log.data?.pages[0]
              ? `${log.data.pages[0].total} entries match these filters`
              : ''}
        </p>
      </div>

      <SummaryStrip />

      <FilterBar
        filters={filters}
        onChange={setFilters}
        clientOptions={clientOptions}
        subfolders={subfolders}
      />

      {log.isError ? (
        <ErrorNotice error={log.error} fallback="The transfer log could not be loaded." />
      ) : null}

      {/*
        No `max-h-[60vh]`: the table takes whatever height is left, down to a floor of
        14rem, so the screen has one scrolling region rather than a page scrollbar around
        a table scrollbar. `aria-busy` marks the rows as stale while a filter reloads —
        they are still the previous filter's rows until the new page arrives.
      */}
      <div
        ref={scrollRef}
        aria-busy={log.isFetching && !log.isFetchingNextPage ? true : undefined}
        className={cx(
          'min-h-[14rem] overflow-auto rounded border border-hairline bg-surface transition-opacity',
          log.isFetching && !log.isFetchingNextPage && !log.isLoading && 'opacity-60',
        )}
      >
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr>
              <SortableHeader label="File" sortKey="fileName" sort={sort} onSort={onSort} />
              <SortableHeader label="Owner" sortKey="ownerClientId" sort={sort} onSort={onSort} />
              <SortableHeader label="Subfolder" sortKey="subfolder" sort={sort} onSort={onSort} />
              <SortableHeader
                label="Acting client"
                sortKey="actorClientId"
                sort={sort}
                onSort={onSort}
              />
              <SortableHeader
                label="Size"
                sortKey="sizeBytes"
                sort={sort}
                onSort={onSort}
                align="right"
                className="text-right"
              />
              <SortableHeader
                label="Time"
                sortKey="time"
                sort={sort}
                onSort={onSort}
                align="right"
                className="text-right"
              />
              <SortableHeader
                label="Status"
                sortKey="status"
                sort={sort}
                onSort={onSort}
                help={{ label: 'What do the STATUS values mean?', items: STATUS_HELP_ITEMS }}
              />
            </tr>
          </thead>
          <tbody>
            {log.isLoading ? <SkeletonRows columns={COLUMN_COUNT} /> : null}

            {!log.isLoading && visible.length === 0 && !log.isError ? (
              <EmptyState
                title="No transfers match these filters"
                hint={emptyHint(applied)}
                columns={COLUMN_COUNT}
                action={
                  hasActiveFilters(applied) ? (
                    <Button onClick={clearFilters}>Clear filters</Button>
                  ) : undefined
                }
              />
            ) : null}

            {visible.map((entry) => {
              const expanded = expandedId === entry.id;
              const serverFault = isServerFault(entry);
              return (
                <Fragment key={entry.id}>
                  <tr
                    onClick={() => setExpandedId(expanded ? null : entry.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setExpandedId(expanded ? null : entry.id);
                      }
                    }}
                    tabIndex={0}
                    aria-expanded={expanded}
                    className={cx(
                      'cursor-pointer border-b border-hairline last:border-0',
                      serverFault ? 'bg-danger-soft' : 'hover:bg-surface-sunken',
                    )}
                  >
                    {/*
                      Wrapped, not truncated. The API itself guarantees a filename is never
                      shortened or altered — see the "Filename rules" section — and an
                      ellipsis in the one screen built to read the log back would quietly
                      take that guarantee away from the reader. `break-words` wraps at a
                      dash or underscore where one exists and, failing that, mid-word, so
                      even a single unbroken run of characters still fits the column instead
                      of spilling out of it.
                    */}
                    <td className="max-w-[18rem] whitespace-normal break-words px-3 py-1.5 align-top text-ink">
                      {displayFileName(entry)}
                    </td>
                    <td className="tabular px-3 py-1.5 align-top text-ink-muted">
                      {entry.ownerClientId ?? '—'}
                    </td>
                    <td className="tabular px-3 py-1.5 align-top text-ink-muted">
                      {displaySubfolder(entry.subfolder)}
                    </td>
                    <td className="tabular px-3 py-1.5 align-top text-ink-muted">
                      {entry.actorClientId ?? '—'}
                    </td>
                    <td className="tabular px-3 py-1.5 text-right align-top text-ink-muted">
                      {entry.sizeBytes === null ? '—' : formatBytes(entry.sizeBytes)}
                    </td>
                    <td
                      className="tabular px-3 py-1.5 text-right align-top text-ink-muted"
                      title={time.rawTimestampTitle(entry.time)}
                    >
                      {time.formatTableTime(entry.time)}
                    </td>
                    <td
                      className={cx(
                        'px-3 py-1.5 align-top font-mono',
                        serverFault ? 'font-medium text-danger' : 'text-ink-muted',
                      )}
                    >
                      {statusLabel(entry)}
                    </td>
                  </tr>
                  {expanded ? (
                    <tr className="border-b border-hairline">
                      <td colSpan={COLUMN_COUNT} className="px-3 py-2">
                        <RowDetail entry={entry} />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>

        <div ref={sentinelRef} className="h-px" aria-hidden="true" />

        {isFetchingNextPage ? (
          <p role="status" className="py-2 text-center text-2xs text-ink-muted">
            Loading older entries…
          </p>
        ) : null}
      </div>
    </div>
  );
}
